import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  Container,
  Text,
} from '@backstage/ui';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import { MarkdownContent } from '@backstage/core-components';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import {
  DefaultTemplateOutputs,
  TaskLogStream,
} from '@backstage/plugin-scaffolder-react/alpha';
import { deleteProjectHref, deleteTemplateParams } from '../home/deleteProject';
import { CancelTaskDialog } from './CancelTaskDialog';
import { DeletionReport } from './DeletionReport';
import { DeletionSteps } from './DeletionSteps';
import { readReportData, stepPhases, type StepView } from './deletionTask';
import { taskProject } from './taskProject';
import { useTaskTemplate } from './taskTemplate';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { CardTitle } from '../ui';

/** The right column while the task runs. */
function RunningCard(props: { running?: StepView; cancelRequested: boolean }) {
  const { running, cancelRequested } = props;
  return (
    <Card>
      <CardHeader>
        <CardTitle
          level={3}
          title={
            running ? `En cours : ${running.name}` : 'En attente du démarrage'
          }
        />
      </CardHeader>
      <CardBody>
        <Text as="p" variant="body-medium" color="secondary">
          {cancelRequested
            ? 'Annulation demandée : aucune autre étape ne sera lancée.'
            : 'Le compte rendu s’affichera ici à la fin. Le journal détaillé reste accessible avec le bouton « Journal ».'}
        </Text>
      </CardBody>
    </Card>
  );
}

/**
 * The task page of the deletion templates (`project-delete`, another
 * type's), in place of the
 * scaffolder's (`plugin.tsx` chooses): the mockup « 2 — SUIVI » while it
 * runs, « 3 — COMPTE RENDU » once done.
 *
 * Left, « Étapes »: the timeline in phases (`DeletionSteps`), then
 * « Annuler » while it runs, « Journal » (the scaffolder's own log viewer,
 * folded until asked for, opened by a failure), « Recommencer » once
 * finished — the template again, same project and same mode, never with
 * `acceptDataLoss` nor `confirmation` (`deleteProjectHref`, as the home
 * page's « Supprimer le projet… »).
 *
 * Right: what runs; then the structured report (`DeletionReport`, from the
 * template's `deletionReport` output); without it — a template or a backend
 * from before it — the template's markdown outputs as the scaffolder shows
 * them; on failure, the failed step and the error.
 *
 * Everything comes from `useTaskEventStream`, the hook the scaffolder's page
 * uses: the task, each step's status and times, the logs, the outputs.
 */
export function ProjectDeletionTaskPage(props: { taskId: string }) {
  const { taskId } = props;
  const stream = useTaskEventStream(taskId);
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const [logsOpen, setLogsOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRequested, setCancelRequested] = useState(false);
  const logsId = useId();

  useEffect(() => {
    if (stream.error) setLogsOpen(true);
  }, [stream.error]);

  const { task } = stream;
  const template = useTaskTemplate(task);
  // The template this task ran from, so a relaunch uses the same.
  const relaunchTemplate = task?.spec.templateInfo?.entityRef;
  const phases = useMemo(
    () => (task ? stepPhases(task, stream.steps) : []),
    [task, stream.steps],
  );

  if (!task) {
    return (
      <Container>
        {stream.error ? (
          <Alert
            mt="4"
            status="danger"
            icon
            title="La tâche n’a pas pu être lue"
            description={stream.error.message}
          />
        ) : (
          <Text as="p" variant="body-medium" color="secondary">
            Chargement de la tâche…
          </Text>
        )}
      </Container>
    );
  }

  const steps = phases.flatMap(phase => phase.steps);
  const running = steps.find(step => step.state === 'running');
  const failed = steps.find(step => step.state === 'failed');
  const finished = stream.completed;
  const cancellable = !finished && !stream.cancelled && !cancelRequested;
  const report =
    finished && !stream.error ? readReportData(stream.output) : undefined;

  const about = taskProject(task, template.appTypes);
  const templateHref =
    relaunchTemplate && templateRoute?.(deleteTemplateParams(relaunchTemplate));
  const restartHref =
    about &&
    templateHref &&
    (about.action === 'deletion' || about.action === 'simulation')
      ? deleteProjectHref(templateHref, about.project, about.action)
      : undefined;

  const title =
    task.spec.templateInfo?.entity?.metadata.title ?? 'Supprimer un projet';

  let main;
  if (stream.error) {
    main = (
      <Alert
        status="danger"
        icon
        role="alert"
        title={
          failed ? `Échec à l’étape « ${failed.name} »` : 'La tâche a échoué'
        }
        description={<MarkdownContent content={stream.error.message} />}
      />
    );
  } else if (!finished) {
    main = (
      <RunningCard
        running={running}
        cancelRequested={cancelRequested || stream.cancelled}
      />
    );
  } else if (report) {
    main = <DeletionReport report={report} output={stream.output ?? {}} />;
  } else {
    main = <DefaultTemplateOutputs output={stream.output} />;
  }

  return (
    <BreadcrumbEntry entry={{ label: taskId, href: taskId }}>
      <Container>
        <div className="waadoo-deletion-task">
          <Text as="h2" variant="title-medium">
            {title}
          </Text>
          <div className="waadoo-deletion-task__layout">
            <Card className="waadoo-deletion-task__steps">
              <CardHeader>
                <CardTitle level={3} title="Étapes" />
              </CardHeader>
              <CardBody>
                <DeletionSteps phases={phases} />
                <div className="waadoo-deletion-task__actions">
                  {cancellable && (
                    <Button
                      variant="secondary"
                      onPress={() => setCancelOpen(true)}
                    >
                      Annuler
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    aria-expanded={logsOpen}
                    aria-controls={logsId}
                    onPress={() => setLogsOpen(open => !open)}
                  >
                    Journal
                  </Button>
                  {finished && restartHref && (
                    <ButtonLink variant="secondary" href={restartHref}>
                      Recommencer
                    </ButtonLink>
                  )}
                </div>
              </CardBody>
            </Card>
            <div className="waadoo-deletion-task__main">{main}</div>
          </div>
          <div id={logsId} hidden={!logsOpen}>
            {logsOpen && (
              <Card>
                <CardHeader>
                  <CardTitle
                    level={3}
                    icon={waadooIcons.logs}
                    title="Journal"
                  />
                </CardHeader>
                <CardBody>
                  <div className="waadoo-deletion-task__log">
                    <TaskLogStream logs={stream.stepLogs} />
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </div>
        <CancelTaskDialog
          taskId={taskId}
          description="Les étapes suivantes ne seront pas lancées. Ce qui a déjà été fait n’est pas défait : relancer le template reprend le démontage là où il s’est arrêté."
          isOpen={cancelOpen}
          onOpenChange={setCancelOpen}
          onCancelled={() => setCancelRequested(true)}
        />
      </Container>
    </BreadcrumbEntry>
  );
}
